/**
 * MyBookingsWidget
 * Dashboard-widget som visar inloggad användares kommande/aktiva bokningar.
 * Vänsterklick → bokningskalendern. Högerklick på bokning → öppna bokningen i kalendern.
 */

import React, { useCallback } from 'react';
import { useTranslation } from '@/store/languageStore';
import { CalendarDaysIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { BaseDashboardWidget } from './BaseDashboardWidget';
import { useNavigate } from 'react-router-dom';

interface MyBookingItem {
  id: string;
  customerName: string | null;
  startAt: string;
  endAt: string;
  status: string;
  pickupLocationName: string;
  pickupLocationId: string;
  vehicleLabel: string;
  priorVehicleLabel?: string | null;
  awaitingVehicleAssignment?: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  BOOKED: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  CHECKED_OUT: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
};

function formatDateShort(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const MyBookingsWidget: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const goToCalendar = useCallback(() => {
    navigate('/booking/calendar');
  }, [navigate]);

  const openBookingInCalendar = useCallback((e: React.MouseEvent, item: MyBookingItem) => {
    e.preventDefault();
    e.stopPropagation();
    const date = new Date(item.startAt).toISOString().slice(0, 10);
    navigate(`/booking/calendar?pickupLocationId=${item.pickupLocationId}&date=${date}&bookingId=${item.id}`);
  }, [navigate]);

  return (
    <BaseDashboardWidget<MyBookingItem>
      icon={CalendarDaysIcon}
      colorScheme="blue"
      title={t('bookingWidget.title')}
      subtitle={t('bookingWidget.title')}
      apiEndpoint="/api/dashboard/data/my-bookings"
      emptyMessage={t('bookingWidget.noBookings')}
      warningBadge={(count) => count > 0 ? `${count} ${t('bookingWidget.upcoming')}` : ''}
      getItemKey={(item) => item.id}
      renderItem={(item) => (
        <div
          className="flex items-center gap-2 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            goToCalendar();
          }}
          onContextMenu={(e) => openBookingInCalendar(e, item)}
        >
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">
              {item.awaitingVehicleAssignment ? t('bookingWidget.noVehicleAssigned') : item.vehicleLabel}
            </div>
            <div className="text-xs text-textSecondary flex gap-2 flex-wrap">
              <span>{item.pickupLocationName}</span>
              <span>•</span>
              <span>{formatDateShort(item.startAt)} – {formatDateShort(item.endAt)}</span>
              {item.awaitingVehicleAssignment ? (
                <span className="text-amber-700 dark:text-amber-300">
                  {t('bookingWidget.awaitingHandling')}
                  {item.priorVehicleLabel ? ` · ${t('bookingWidget.previousVehicle')} ${item.priorVehicleLabel}` : ''}
                </span>
              ) : null}
            </div>
          </div>
          <span className={`text-xs px-1.5 py-0.5 rounded whitespace-nowrap ${
            item.awaitingVehicleAssignment
              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
              : (STATUS_COLORS[item.status] || STATUS_COLORS.BOOKED)
          }`}>
            {item.awaitingVehicleAssignment
              ? t('bookingWidget.awaiting')
              : item.status === 'CHECKED_OUT' ? t('bookingWidget.checkedOut') : t('bookingWidget.booked')}
          </span>
        </div>
      )}
      footerAction={
        <button
          onClick={(e) => { e.stopPropagation(); goToCalendar(); }}
          className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline mt-2"
        >
          <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
          {t('bookingWidget.openCalendar')}
        </button>
      }
    />
  );
};

export default MyBookingsWidget;
